import { useId, useState } from 'react';
import { Button, useToast } from '../../../ds/components';
import type { FinancingTabProps } from '../record/RecordFrame';
import { useChecklist, useRefreshFinancing, type FinancierRecordFields } from './api';
import { requestOfferUpload } from './OfferUpload';
import { RequestDocumentsDialog } from './RequestDocumentsDialog';
import { RequestNoteDialog } from './RequestNoteDialog';
import { SignatureDialog } from './SignatureDialog';
import styles from './financier.module.css';

/**
 * The financier's header buttons of the record (Finanzierer 03–11) from the record's `actions`:
 * „Unterlagen anfordern" + „Als angefragt vermerken" (disabled with the server's reason until the documents are
 * complete), „Angebot hochladen" on the offers tab, „Nachricht an Berater" + „Fina-Unterschrift erfassen" in „Zusage".
 */

const BEFORE_SIGNATURE = new Set(['documents', 'requested', 'offers', 'commitment']);
const UPLOAD_STAGES = new Set(['requested', 'offers', 'commitment']);

export function FinancierHeaderActions(props: FinancingTabProps & { tab: string; onTab(tab: string): void }) {
  const record: FinancierRecordFields = props.financing;
  const tab = props.tab;
  const reasonId = useId();
  const refresh = useRefreshFinancing(record.id);
  const { toast } = useToast();
  const [requestOpen, setRequestOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const stage = record.stage.key;
  const active = record.status === 'active';
  const checklist = useChecklist(record.id, requestOpen);

  if (!active) return null;

  const { requestDocuments, markRequested, uploadOffer, recordSignature } = record.actions;
  // FZ-03/04/05: on the documents tab and while the documents are gathered; not next to the offers or the signature
  const showRequest =
    requestDocuments.allowed && BEFORE_SIGNATURE.has(stage) && (stage === 'documents' || tab === 'documents');
  const showMark = stage === 'documents';
  const showUpload = uploadOffer.allowed && UPLOAD_STAGES.has(stage) && tab === 'offers';
  const commitment = stage === 'commitment';

  async function changed() {
    await refresh();
    props.onChanged();
  }

  function upload() {
    if (!requestOfferUpload(record.id)) props.onTab('offers');
  }

  return (
    <div className={styles.headerActions}>
      {showRequest ? (
        <Button variant="secondary" onClick={() => setRequestOpen(true)}>
          Unterlagen anfordern
        </Button>
      ) : null}
      {showMark ? (
        <>
          <Button
            disabled={!markRequested.allowed}
            title={markRequested.allowed ? undefined : (markRequested.reason ?? undefined)}
            aria-describedby={markRequested.allowed ? undefined : reasonId}
            onClick={() => setNoteOpen(true)}
          >
            Als angefragt vermerken
          </Button>
          {!markRequested.allowed && markRequested.reason ? (
            <span id={reasonId} className={styles.srOnly}>
              {markRequested.reason}
            </span>
          ) : null}
        </>
      ) : null}
      {showUpload ? (
        <Button variant="secondary" onClick={upload}>
          Angebot hochladen
        </Button>
      ) : null}
      {commitment && record.advisor ? (
        <a className={`pressable ${styles.linkButton}`} href={`mailto:${record.advisor.email}`}>
          Nachricht an Berater
        </a>
      ) : null}
      {commitment && recordSignature.allowed && tab !== 'overview' ? (
        <Button onClick={() => setSignatureOpen(true)}>Fina-Unterschrift erfassen</Button>
      ) : null}
      {checklist.data ? (
        <RequestDocumentsDialog
          open={requestOpen}
          financing={record}
          requestable={checklist.data.requestable}
          onClose={() => setRequestOpen(false)}
          onDone={() => {
            setRequestOpen(false);
            toast({ message: 'Die Anforderung ist unterwegs.' });
            void changed();
          }}
        />
      ) : null}
      <RequestNoteDialog
        open={noteOpen}
        financing={record}
        onClose={() => setNoteOpen(false)}
        onDone={() => {
          setNoteOpen(false);
          void changed();
        }}
      />
      <SignatureDialog financing={record} open={signatureOpen} onClose={() => setSignatureOpen(false)} />
    </div>
  );
}
